import { useEffect, useId, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

export type NavDropdownItem = { to: string; labelKey: string };

type Props = {
  /** The parent tab: still a normal link to its own page. */
  to: string;
  labelKey: string;
  /** Accessible name for the chevron, e.g. "Sponsors menu". */
  menuLabelKey: string;
  items: ReadonlyArray<NavDropdownItem>;
  /** Shared with the plain tabs so all of them look identical. */
  linkClassName: (state: { isActive: boolean }) => string;
};

/*
 * One desktop nav tab that also opens a small menu — the entry point the client
 * chose for the sponsor videos ("Sponsors" → All sponsors / Watch sponsor
 * videos). The parent stays a real link to its own page, so nothing is lost for
 * anyone who just clicks it.
 *
 * This is the W3C disclosure-navigation pattern, NOT role="menu": a menu role
 * would promise arrow-key roving focus that plain links don't implement. The
 * chevron carries aria-expanded, and the panel is an ordinary list of links, so
 * Tab moves through them as usual.
 *
 * Hand-rolled because no dropdown/popover primitive is installed here, and
 * Radix's dropdown deliberately does not open on hover — hover is what the
 * approved prototype did.
 *
 * Opens on hover, on keyboard focus, and on click. Closes on Escape (focus back
 * to the chevron), on any outside pointer press, when the route changes, and on
 * scroll — the header shrinks as you scroll past the hero, which would leave an
 * open panel visibly detached from its tab.
 */
export function NavDropdown({ to, labelKey, menuLabelKey, items, linkClassName }: Props) {
  const { t } = useTranslation("common");
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onScroll = () => setOpen(false);

    document.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("scroll", onScroll);
    };
  }, [open]);

  // Navigating away (including via an item in here) closes the panel.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <div
      ref={wrapperRef}
      className="relative flex items-center"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocusCapture={() => setOpen(true)}
      onBlur={(event) => {
        // Only close when focus actually leaves the tab + its panel.
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
      }}
      onKeyDown={(event) => {
        if (event.key !== "Escape" || !open) return;
        event.stopPropagation();
        setOpen(false);
        toggleRef.current?.focus();
      }}
    >
      <NavLink to={to} className={linkClassName}>
        {t(labelKey)}
      </NavLink>
      <button
        ref={toggleRef}
        type="button"
        aria-label={t(menuLabelKey)}
        aria-expanded={open}
        aria-haspopup="true"
        aria-controls={panelId}
        onClick={() => setOpen((s) => !s)}
        className="ml-0.5 inline-flex h-6 w-5 items-center justify-center rounded text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <ChevronDown
          aria-hidden
          className={cn(
            "h-4 w-4 transition-transform duration-150 motion-reduce:transition-none",
            open && "rotate-180"
          )}
        />
      </button>

      {open && (
        /*
         * pt-3 is a deliberate hover bridge: without it the pointer crosses a
         * dead gap between the tab and the panel and the panel closes before
         * it can be used.
         */
        <div
          id={panelId}
          className="absolute left-1/2 top-full z-50 -translate-x-1/2 pt-3"
        >
          <ul className="min-w-[14rem] rounded-lg border border-border bg-card p-1.5 shadow-lg">
            {items.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      "block whitespace-nowrap rounded-md px-3.5 py-2.5 text-sm font-medium leading-5 transition-colors",
                      isActive
                        ? "font-semibold text-primary"
                        : "text-foreground/80 hover:bg-primary/10 hover:text-primary focus-visible:bg-primary/10 focus-visible:text-primary"
                    )
                  }
                >
                  {t(item.labelKey)}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
